/**
 * OpportunityFilters Component
 * Sidebar filter panel for opportunity browsing
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Form, Button, Accordion } from 'react-bootstrap';

import '../Opportunities.css';

// Group opportunity types by category for better UX
const TYPE_CATEGORIES = {
    'Work-Based Learning': [
        'Internship',
        'Job Shadow',
        'Apprenticeship',
        'Co-op Program',
        'Job/Employment',
    ],
    'Mentoring & Support': ['Mentorship', 'Coaching Program', 'Peer Mentoring'],
    'Programs & Camps': [
        'Summer Program',
        'After-School Program',
        'Camp',
        'Bootcamp',
        'Cohort Program',
    ],
    'Workshops & Training': [
        'Workshop',
        'Training Session',
        'Certification Course',
        'Professional Development',
        'Webinar',
        'Seminar',
    ],
    'Events & Networking': [
        'Networking Event',
        'Conference',
        'Career Fair',
        'Industry Tour',
        'Open House',
    ],
    Competitions: ['Competition', 'Hackathon', 'Science Fair', 'Showcase/Expo'],
    'Funding & Scholarships': [
        'Grant Opportunity',
        'Funding Partnership',
        'Scholarship',
        'Fellowship',
        'Sponsorship',
    ],
    'Community & Volunteer': [
        'Volunteer',
        'Community Project',
        'Service Learning',
    ],
};

export default function OpportunityFilters({
    filters,
    filterOptions,
    onFilterChange,
}) {
    const handleTypeChange = (type) => {
        onFilterChange({
            ...filters,
            type: filters.type === type ? '' : type,
        });
    };

    const handleAudienceChange = (audience) => {
        onFilterChange({
            ...filters,
            audience: filters.audience === audience ? '' : audience,
        });
    };

    const handleRegionChange = (region) => {
        onFilterChange({
            ...filters,
            region: filters.region === region ? '' : region,
        });
    };

    const clearFilters = () => {
        onFilterChange({
            type: '',
            audience: '',
            region: '',
            search: '',
        });
    };

    const hasActiveFilters =
        filters.type || filters.audience || filters.region || filters.search;

    return (
        <Card className="border-0 shadow-sm opportunity-filters">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
                <strong>Filters</strong>
                {hasActiveFilters && (
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-muted"
                        onClick={clearFilters}
                    >
                        Clear all
                    </Button>
                )}
            </Card.Header>
            <Card.Body className="p-0">
                <Accordion defaultActiveKey={[]} alwaysOpen flush>
                    {/* Opportunity Type */}
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>Opportunity Type</Accordion.Header>
                        <Accordion.Body className="pt-0">
                            <div className="filter-checkbox-list">
                                {Object.entries(TYPE_CATEGORIES).map(
                                    ([category, types]) => (
                                        <div key={category} className="mb-3">
                                            <small className="text-muted d-block mb-1">
                                                {category}
                                            </small>
                                            {types.map((type) => (
                                                <Form.Check
                                                    key={type}
                                                    type="radio"
                                                    id={`type-${type}`}
                                                    label={type}
                                                    checked={
                                                        filters.type === type
                                                    }
                                                    onChange={() =>
                                                        handleTypeChange(type)
                                                    }
                                                    className="small"
                                                />
                                            ))}
                                        </div>
                                    )
                                )}
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>

                    {/* Target Audience */}
                    <Accordion.Item eventKey="1">
                        <Accordion.Header>Who It&apos;s For</Accordion.Header>
                        <Accordion.Body className="pt-0">
                            <div className="filter-checkbox-list">
                                {filterOptions.targetAudiences?.map(
                                    (audience) => (
                                        <Form.Check
                                            key={audience}
                                            type="radio"
                                            id={`audience-${audience}`}
                                            label={audience}
                                            checked={
                                                filters.audience === audience
                                            }
                                            onChange={() =>
                                                handleAudienceChange(audience)
                                            }
                                            className="small"
                                        />
                                    )
                                )}
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>

                    {/* Region */}
                    <Accordion.Item eventKey="2">
                        <Accordion.Header>Region</Accordion.Header>
                        <Accordion.Body className="pt-0">
                            <div className="filter-checkbox-list">
                                {filterOptions.regions?.map((region) => (
                                    <Form.Check
                                        key={region}
                                        type="radio"
                                        id={`region-${region}`}
                                        label={region}
                                        checked={filters.region === region}
                                        onChange={() =>
                                            handleRegionChange(region)
                                        }
                                        className="small"
                                    />
                                ))}
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Card.Body>
        </Card>
    );
}

OpportunityFilters.propTypes = {
    filters: PropTypes.shape({
        type: PropTypes.string,
        audience: PropTypes.string,
        region: PropTypes.string,
        search: PropTypes.string,
    }).isRequired,
    filterOptions: PropTypes.shape({
        opportunityTypes: PropTypes.arrayOf(PropTypes.string),
        targetAudiences: PropTypes.arrayOf(PropTypes.string),
        regions: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    onFilterChange: PropTypes.func.isRequired,
};
